import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { defineStore } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { useNotification } from "@/hooks/notifications";
import { aiAgentsApiService } from "@/services/modules/aiAgents";
import { useAnalyticsStore } from "@/stores/analytics";

import type { MessageContentBlock } from "@/models/aiAgents";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const allowedDocTypes = [
  "pdf",
  "gif",
  "jpeg",
  "png",
  "xls",
  "xlst",
  "doc",
  "docx",
  "xlsx",
  "heic",
  "p12",
  "pfx",
  "pem",
] as const;

export const useFiles = defineStore("aiFiles", () => {
  const { t } = useI18n();
  const { showMessage } = useNotification();
  const { trackEvent } = useAnalyticsStore();

  const copiedFiles = ref<File[]>([]);
  const failedFiles = ref<Set<File>>(new Set());
  const invalidFiles = ref<Set<File>>(new Set());
  const selectedFiles = ref<File[]>([]);

  const isValidChatFileType = (file: File): boolean => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return extension
      ? allowedDocTypes.some((type) => type === extension)
      : false;
  };

  const isValidChatFileSize = (fileSize: number): boolean => {
    return fileSize <= MAX_FILE_SIZE;
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files?.length) {
      return;
    }

    const filesArray = Array.from(files);

    for (const file of filesArray) {
      const isValidType = isValidChatFileType(file);
      const isValidSize = isValidChatFileSize(file.size);

      if (!isValidType || !isValidSize) {
        invalidFiles.value = invalidFiles.value.add(file);
        const message = !isValidType
          ? t("AI_AGENTS.FILE_UPLOAD.INVALID_TYPE", {
              fileName: file.name,
            })
          : t("AI_AGENTS.FILE_UPLOAD.FILE_TOO_LARGE", {
              fileName: file.name,
            });
        trackEvent(AnalyticsEvent.CmdKFileUploadFileError, {
          message,
          fileName: file.name,
        });
        showMessage(message, "error");
        break;
      }

      selectedFiles.value.push(file);
    }
  };

  const removeFile = (index: number) => {
    const file = selectedFiles.value[index];

    if (!file) {
      return;
    }

    invalidFiles.value.delete(file);
    failedFiles.value.delete(file);

    selectedFiles.value.splice(index, 1);
  };

  const clearFiles = () => {
    selectedFiles.value = [];
    invalidFiles.value = new Set();
    failedFiles.value = new Set();
  };

  const clearInvalidFiles = () => {
    invalidFiles.value = new Set();
  };

  const clearFailedFiles = () => {
    failedFiles.value = new Set();
  };

  const copySelectedFiles = () => {
    copiedFiles.value = [...selectedFiles.value];
  };

  const clearCopiedFiles = () => {
    copiedFiles.value = [];
  };

  const createFileBlock = (
    fileId: number,
    fileName: string
  ): MessageContentBlock => ({
    type: "file",
    file_id: fileId,
    file_name: fileName,
  });

  const uploadFiles = async (
    filesToUpload: File[],
    sessionId: number
  ): Promise<{ fileBlocks: MessageContentBlock[] }> => {
    const fileBlocks: MessageContentBlock[] = [];

    clearFailedFiles();
    clearInvalidFiles();

    const uploadPromises = filesToUpload.map(async (file) => {
      try {
        const { file_id } = await aiAgentsApiService.uploadFile(
          sessionId,
          file
        );
        return { file, file_id, success: true };
      } catch (error) {
        failedFiles.value.add(file);
        return { file, error, success: false };
      }
    });

    const results = await Promise.all(uploadPromises);

    // Process successful uploads
    for (const result of results) {
      if (result.success) {
        fileBlocks.push(createFileBlock(result.file_id!, result.file.name));
      }
    }

    // If any uploads failed, throw an error
    const failedUploads = results.filter((r) => !r.success);
    if (failedUploads.length > 0) {
      throw new Error(`Failed to upload ${failedUploads.length} file(s)`);
    }

    return { fileBlocks };
  };

  return {
    allowedDocTypes,
    selectedFiles,
    invalidFiles,
    failedFiles,
    copiedFiles,
    handleFileUpload,
    removeFile,
    clearFiles,
    clearFailedFiles,
    clearInvalidFiles,
    copySelectedFiles,
    clearCopiedFiles,
    uploadFiles,
  };
});
